import { LoadingRegion } from "@commerce/components/page";
import { SourceScreen } from "@commerce/sources/SourceScreen";
import { sourceSearchSchema } from "@commerce/sources/search-params";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute, notFound } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/sources/$sourceId",
)({
	validateSearch: sourceSearchSchema,
	// Tabs, filters and the open run are client state; the loader only warms the source.
	shouldReload: false,
	loader: async ({ context, params }) => {
		const queryClient = getServerQueryClient();
		const data = await queryClient
			.ensureQueryData(
				orpc.catalog.sources.get.queryOptions({
					input: { sourceId: params.sourceId },
				}),
			)
			.catch(() => null);
		// Unknown, or a source of another retailer reached through this one's address.
		if (!data || data.source.organizationId !== context.organization.id) {
			throw notFound();
		}
		return {
			name: data.source.name,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: `${loaderData?.name ?? "Source"} - ${config.appName}`,
			},
		],
	}),
	component: SourcePage,
});

function SourcePage() {
	const { sourceId } = Route.useParams();
	const { dehydratedState } = Route.useLoaderData();
	return (
		<AsyncBoundary
			fallback={
				<LoadingRegion
					label="Loading the source"
					className="min-h-[50svh]"
				/>
			}
			dehydratedState={dehydratedState}
			resetKeys={[sourceId]}
		>
			<SourceScreen sourceId={sourceId} />
		</AsyncBoundary>
	);
}
